import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Copy, Download, KeyRound, ListChecks, Loader2, Package, Plug, Plus, Trash2, Zap } from 'lucide-react';
import { api } from '../../services/api';

interface Props {
  projectId: string;
  token: string;
}

interface McpConfig {
  mcpEnabled: boolean;
  mcpWriteEnabled: boolean;
  accessLevel: 'read' | 'write' | null;
  canManage: boolean;
}

interface McpTokenSummary {
  tokenId: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  label: string;
  hint: string;
  createdAt: string;
  expiresAt: string;
  lastUsedAt?: string;
  status: 'active' | 'expired' | 'revoked';
}

const EXPIRY_OPTIONS = [30, 90, 180, 365] as const;
const MAX_ACTIVE_TOKENS = 5;

const MCP_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/mcp`;

async function apiFetch(path: string, token: string, method = 'GET', data?: unknown) {
  try {
    const res = await api.request({
      url: `/api${path}`,
      method,
      data,
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.data.success) throw new Error(res.data.error ?? 'Request failed');
    return res.data;
  } catch (e: any) {
    throw new Error(e?.response?.data?.error ?? e.message ?? 'Request failed');
  }
}

const formatDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

interface StatusCardProps {
  enabled: boolean | undefined;
  writeEnabled: boolean | undefined;
  canManage: boolean;
  working: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onToggleWrite: (next: boolean) => void;
}

const StatusCard: React.FC<StatusCardProps> = ({ enabled, writeEnabled, canManage, working, onEnable, onDisable, onToggleWrite }) => (
  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
    <div className={`p-5 ${enabled ? 'border-b border-[#e2e8f0]' : ''}`}>
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-4">Status</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${enabled ? 'bg-green-500' : 'bg-slate-300'}`} />
          <span className="text-sm font-semibold text-navy">
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
          <span className="text-sm text-slate-500">
            {enabled
              ? 'Claude Code can connect to this project.'
              : canManage
                ? 'Enable MCP to allow Claude Code to read project requirements.'
                : 'Ask the project owner or a member with write access to enable MCP.'}
          </span>
        </div>
        <div className={`flex items-center gap-2 ${canManage ? '' : 'hidden'}`}>
          {enabled ? (
            <button
              onClick={onDisable}
              disabled={working}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
            >
              Disable
            </button>
          ) : (
            <button
              data-training-id="enable-mcp-button"
              onClick={onEnable}
              disabled={working}
              className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
            >
              <Plug size={16} /> Enable MCP
            </button>
          )}
        </div>
      </div>
    </div>

    {enabled && (
      <div className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-navy">Write access</p>
            <p className="text-sm text-slate-500">
              {writeEnabled
                ? 'Members with write access can create, update and delete project data. Read-access members stay read-only.'
                : 'Claude Code can only read project data.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onToggleWrite(!writeEnabled)}
            disabled={working || !canManage}
            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none disabled:opacity-50 ${writeEnabled ? 'bg-primary' : 'bg-slate-300'}`}
            role="switch"
            aria-checked={!!writeEnabled}
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${writeEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>
      </div>
    )}
  </div>
);

interface RevealedTokenProps {
  value: string;
  copied: boolean;
  onCopy: () => void;
  onDismiss: () => void;
}

const RevealedToken: React.FC<RevealedTokenProps> = ({ value, copied, onCopy, onDismiss }) => (
  <div className="flex flex-col gap-3 p-5 border-b border-[#e2e8f0] bg-amber-50/40">
    <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-xl">
      <code className="flex-1 text-xs font-mono text-navy break-all">{value}</code>
      <button onClick={onCopy} className="flex-shrink-0 flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors">
        {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
      </button>
    </div>
    <div className="flex items-center justify-between">
      <p className="text-xs text-amber-600 flex items-center gap-1.5">
        <AlertTriangle size={13} /> Copy this token now. It will not be shown again. The config below already includes it.
      </p>
      <button onClick={onDismiss} className="text-xs font-semibold text-slate-500 hover:text-slate-700">Done</button>
    </div>
  </div>
);

interface CreateTokenFormProps {
  working: boolean;
  onCreate: (label: string, expiresInDays: number) => void;
  onCancel: () => void;
}

const CreateTokenForm: React.FC<CreateTokenFormProps> = ({ working, onCreate, onCancel }) => {
  const [label, setLabel] = useState('');
  const [expiry, setExpiry] = useState<number>(90);
  return (
    <form
      className="p-5 border-b border-[#e2e8f0] flex flex-col gap-4"
      onSubmit={(e) => { e.preventDefault(); if (label.trim()) onCreate(label.trim(), expiry); }}
    >
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">Name</label>
          <input
            autoFocus
            value={label}
            maxLength={60}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. MacBook, CI pipeline"
            className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-navy mb-2">Expires after</label>
          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
            {EXPIRY_OPTIONS.map((days) => (
              <button
                key={days}
                type="button"
                onClick={() => setExpiry(days)}
                className={`flex-1 py-2.5 text-xs font-semibold transition-all ${expiry === days ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2">
        <button type="button" onClick={onCancel} className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors">
          Cancel
        </button>
        <button type="submit" disabled={working || !label.trim()} className="flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50">
          <KeyRound size={16} /> Create token
        </button>
      </div>
    </form>
  );
};

const STATUS_CHIP: Record<McpTokenSummary['status'], string> = {
  active: 'bg-green-50 text-green-600 border-green-200',
  expired: 'bg-slate-50 text-slate-500 border-slate-200',
  revoked: 'bg-red-50 text-red-600 border-red-200',
};

interface TokenTableProps {
  tokens: McpTokenSummary[];
  showOwner: boolean;
  working: boolean;
  onRevoke: (token: McpTokenSummary) => void;
}

const TokenTable: React.FC<TokenTableProps> = ({ tokens, showOwner, working, onRevoke }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[#e2e8f0] bg-slate-50/50 text-left text-xs font-semibold text-slate-500">
          <th className="px-5 py-2.5">Name</th>
          {showOwner && <th className="px-5 py-2.5">Member</th>}
          <th className="px-5 py-2.5">Token</th>
          <th className="px-5 py-2.5">Created</th>
          <th className="px-5 py-2.5">Expires</th>
          <th className="px-5 py-2.5">Last used</th>
          <th className="px-5 py-2.5">Status</th>
          <th className="px-5 py-2.5" />
        </tr>
      </thead>
      <tbody className="divide-y divide-[#e2e8f0]">
        {tokens.map((t) => (
          <tr key={t.tokenId} className={t.status === 'active' ? '' : 'text-slate-400'}>
            <td className="px-5 py-3 font-semibold text-navy">{t.label}</td>
            {showOwner && <td className="px-5 py-3 text-slate-600">{t.userName ?? t.userEmail ?? t.userId}</td>}
            <td className="px-5 py-3 font-mono text-xs">spm_…{t.hint}</td>
            <td className="px-5 py-3">{formatDate(t.createdAt)}</td>
            <td className="px-5 py-3">{formatDate(t.expiresAt)}</td>
            <td className="px-5 py-3">{t.lastUsedAt ? formatDate(t.lastUsedAt) : 'Never'}</td>
            <td className="px-5 py-3">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_CHIP[t.status]}`}>{t.status}</span>
            </td>
            <td className="px-5 py-3 text-right">
              {t.status === 'active' && (
                <button
                  onClick={() => onRevoke(t)}
                  disabled={working}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 size={13} /> Revoke
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

interface TokensCardProps {
  title: string;
  description: string;
  tokens: McpTokenSummary[];
  showOwner: boolean;
  working: boolean;
  onRevoke: (token: McpTokenSummary) => void;
  action?: React.ReactNode;
  children?: React.ReactNode;
}

const TokensCard: React.FC<TokensCardProps> = ({ title, description, tokens, showOwner, working, onRevoke, action, children }) => (
  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
    <div className="p-5 border-b border-[#e2e8f0] flex items-start justify-between gap-4">
      <div>
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-1">{title}</p>
        <p className="text-sm text-slate-500">{description}</p>
      </div>
      {action}
    </div>
    {children}
    {tokens.length === 0 ? (
      <p className="p-5 text-sm text-slate-400">No tokens yet.</p>
    ) : (
      <TokenTable tokens={tokens} showOwner={showOwner} working={working} onRevoke={onRevoke} />
    )}
  </div>
);

interface SnippetCardProps {
  configSnippet: string;
  snippetCopied: boolean;
  onCopySnippet: () => void;
  writeEnabled: boolean;
}

const READ_TOOLS_LABEL = 'get_project_info, get_token_usage, list_sessions, get_session_details, get_personas, get_features, get_user_stories, get_requirements, get_adrs, get_challenge_areas, get_stakeholders';
const WRITE_TOOLS_LABEL = 'create/update/delete_persona, create/update/delete_user_story, create/update/delete_feature, create/update/delete_nfr, create/update/delete_adr, upsert_challenge_area, create/update/delete_stakeholder';

const SnippetCard: React.FC<SnippetCardProps> = ({ configSnippet, snippetCopied, onCopySnippet, writeEnabled }) => (
  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
    <div className="p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Claude Code Config</p>
        <button onClick={onCopySnippet} className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary-dark transition-colors">
          {snippetCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
        </button>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Add this to your <code className="font-mono text-xs text-navy">~/.claude/mcp.json</code> (or project-level <code className="font-mono text-xs text-navy">.mcp.json</code>):
      </p>
      <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs font-mono text-navy overflow-x-auto whitespace-pre leading-relaxed">
        {configSnippet}
      </pre>
      <p className="text-xs text-slate-400 mt-3">
        Read tools: <code className="font-mono">{READ_TOOLS_LABEL}</code>
      </p>
      {writeEnabled && (
        <p className="text-xs text-slate-400 mt-2">
          Write tools: <code className="font-mono">{WRITE_TOOLS_LABEL}</code>
        </p>
      )}
    </div>
  </div>
);

interface McpSkillArtifact {
  id: string;
  name: string;
  version: string;
  updated: string | null;
  description: string;
  argumentHint: string | null;
  howToUse: string[];
}

const SKILL_TITLES: Record<string, string> = {
  'prd-builder': 'PRD Builder',
  'workshop-report': 'Workshop Report',
  'estimate-builder': 'Estimate Builder',
  'session-animation': 'Session Animation',
};

const FLOW_STEPS = [
  'Run co-creation sessions to capture the transcript',
  'Extract User Stories, Personas, NFRs, Features and ADRs in this platform',
  'Connect Claude Code to this project via MCP (above)',
  'Use MCP to pull requirements, personas, features etc. into Claude Code',
  'Use the skills below to build the PRD set and Workshop Report',
  'Start the estimate process with Estimate Builder',
];

const FlowGuide: React.FC = () => (
  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
    <div className="p-5">
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-4">Delivery Flow</p>
      <ol className="flex flex-col gap-2.5">
        {FLOW_STEPS.map((step, i) => (
          <li key={step} className="flex items-start gap-3 text-sm text-slate-600">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </span>
            {step}
          </li>
        ))}
      </ol>
    </div>
  </div>
);

interface SkillCardProps {
  skill: McpSkillArtifact;
  downloading: boolean;
  onDownload: () => void;
}

const SkillCard: React.FC<SkillCardProps> = ({ skill, downloading, onDownload }) => (
  <div className="border border-[#e2e8f0] rounded-xl p-4 flex flex-col gap-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Package size={14} />
        </div>
        <div>
          <p className="text-sm font-semibold text-navy">{SKILL_TITLES[skill.id] ?? skill.name}</p>
          <p className="text-xs text-slate-400">
            v{skill.version}{skill.updated ? ` · Updated ${skill.updated}` : ''}
          </p>
        </div>
      </div>
      <button
        onClick={onDownload}
        disabled={downloading}
        className="flex-shrink-0 flex items-center justify-center gap-1.5 rounded-xl h-9 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
      >
        {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        {downloading ? 'Preparing…' : 'Download'}
      </button>
    </div>

    <p className="text-sm text-slate-600 whitespace-pre-line">{skill.description}</p>

    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
      <p className="text-[11px] font-bold text-slate-500 tracking-wide uppercase mb-2 flex items-center gap-1.5">
        <ListChecks size={12} /> How to use
      </p>
      <ol className="flex flex-col gap-1">
        {skill.howToUse.map((step, i) => (
          <li key={step} className="text-xs text-slate-600">
            {i + 1}. {step}
          </li>
        ))}
      </ol>
    </div>
  </div>
);

interface SkillsSectionProps {
  skills: McpSkillArtifact[];
  loading: boolean;
  error: string | null;
  downloadingId: string | null;
  onDownload: (skillId: string) => void;
}

const SkillsSection: React.FC<SkillsSectionProps> = ({ skills, loading, error, downloadingId, onDownload }) => (
  <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
    <div className="p-5">
      <p className="text-[11px] font-bold text-primary tracking-widest uppercase mb-1">Downloadable Skills</p>
      <p className="text-sm text-slate-500 mb-4">
        Claude Code skills that consume this project via MCP to build delivery artefacts.
      </p>

      {loading ? (
        <div className="flex justify-center items-center py-10 text-slate-400 text-sm">Loading…</div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : (
        <div className="flex flex-col gap-3">
          {skills.map((skill) => (
            <SkillCard
              key={skill.id}
              skill={skill}
              downloading={downloadingId === skill.id}
              onDownload={() => onDownload(skill.id)}
            />
          ))}
        </div>
      )}
    </div>
  </div>
);

export const McpServerTab: React.FC<Props> = ({ projectId, token }) => {
  const [config, setConfig] = useState<McpConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [tokens, setTokens] = useState<McpTokenSummary[]>([]);
  const [canManageAll, setCanManageAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [snippetCopied, setSnippetCopied] = useState(false);

  const [skills, setSkills] = useState<McpSkillArtifact[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [skillsError, setSkillsError] = useState<string | null>(null);
  const [downloadingSkillId, setDownloadingSkillId] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, tokenData] = await Promise.all([
        apiFetch(`/projects/${projectId}/mcp-config`, token),
        apiFetch(`/projects/${projectId}/mcp-tokens`, token),
      ]);
      setConfig({ mcpEnabled: data.mcpEnabled, mcpWriteEnabled: data.mcpWriteEnabled, accessLevel: data.accessLevel, canManage: data.canManage });
      setCurrentUserId(data.userId ?? null);
      setTokens(tokenData.tokens ?? []);
      setCanManageAll(!!tokenData.canManageAll);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  const loadTokens = useCallback(async () => {
    const data = await apiFetch(`/projects/${projectId}/mcp-tokens`, token);
    setTokens(data.tokens ?? []);
    setCanManageAll(!!data.canManageAll);
  }, [projectId, token]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  useEffect(() => {
    (async () => {
      try {
        setSkillsLoading(true);
        const data = await apiFetch(`/projects/${projectId}/mcp-config/skills`, token);
        setSkills(data.skills ?? []);
      } catch (e: any) {
        setSkillsError(e.message);
      } finally {
        setSkillsLoading(false);
      }
    })();
  }, [projectId, token]);

  async function handleDownloadSkill(skillId: string) {
    setDownloadingSkillId(skillId);
    try {
      const res = await api.request({
        url: `/api/projects/${projectId}/mcp-config/skills/${skillId}/download`,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${skillId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setSkillsError(e.message ?? 'Download failed');
    } finally {
      setDownloadingSkillId(null);
    }
  }

  async function runMcpAction(fn: () => Promise<void>) {
    setWorking(true);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleEnable() {
    await runMcpAction(async () => {
      await apiFetch(`/projects/${projectId}/mcp-config/enable`, token, 'POST');
      setConfig((prev) => prev ? { ...prev, mcpEnabled: true } : prev);
    });
  }

  async function handleCreateToken(label: string, expiresInDays: number) {
    await runMcpAction(async () => {
      const data = await apiFetch(`/projects/${projectId}/mcp-tokens`, token, 'POST', { label, expiresInDays });
      setCreating(false);
      setRevealedToken(data.token);
      await loadTokens();
    });
  }

  async function handleRevokeToken(t: McpTokenSummary) {
    const whose = t.userId === currentUserId ? '' : ` belonging to ${t.userName ?? t.userEmail ?? t.userId}`;
    if (!window.confirm(`Revoke "${t.label}"${whose}? Any Claude Code client using it will stop working immediately.`)) return;
    await runMcpAction(async () => {
      await apiFetch(`/projects/${projectId}/mcp-tokens/${t.tokenId}`, token, 'DELETE');
      await loadTokens();
    });
  }

  async function handleDisable() {
    await runMcpAction(async () => {
      await apiFetch(`/projects/${projectId}/mcp-config/disable`, token, 'POST');
      setConfig((prev) => prev ? { ...prev, mcpEnabled: false, mcpWriteEnabled: false } : prev);
    });
  }

  async function handleToggleWrite(next: boolean) {
    await runMcpAction(async () => {
      await apiFetch(`/projects/${projectId}/mcp-config/write/${next ? 'enable' : 'disable'}`, token, 'POST');
      setConfig((prev) => prev ? { ...prev, mcpWriteEnabled: next } : prev);
    });
  }

  function copyToken() {
    if (!revealedToken) return;
    navigator.clipboard.writeText(revealedToken);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const myTokens = tokens.filter((t) => t.userId === currentUserId);
  const otherTokens = tokens.filter((t) => t.userId !== currentUserId);
  const myActiveCount = myTokens.filter((t) => t.status === 'active').length;
  const canWrite = !!config?.mcpWriteEnabled && config?.accessLevel === 'write';
  const tokenDisplay = revealedToken ?? '<your personal token>';
  const configSnippet = `{
  "mcpServers": {
    "v1cc": {
      "type": "http",
      "url": "${MCP_BASE}/${projectId}",
      "headers": {
        "Authorization": "Bearer ${tokenDisplay}"
      }
    }
  }
}`;

  function copySnippet() {
    navigator.clipboard.writeText(configSnippet);
    setSnippetCopied(true);
    setTimeout(() => setSnippetCopied(false), 2000);
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Zap size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">MCP Server</h1>
              <p className="text-slate-500 text-sm font-medium">
                Connect Claude Code to this project via the Model Context Protocol
              </p>
            </div>
          </div>
        </header>

        {/* Error banner */}
        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 flex items-center gap-3">
            <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>
        ) : (
          <>
            <StatusCard
              enabled={config?.mcpEnabled}
              writeEnabled={config?.mcpWriteEnabled}
              canManage={!!config?.canManage}
              working={working}
              onEnable={handleEnable}
              onDisable={handleDisable}
              onToggleWrite={handleToggleWrite}
            />

            {config?.accessLevel && (
              <TokensCard
                title="Your Access Tokens"
                description={`Personal tokens for this project. Claude Code acts as you, with ${canWrite ? 'write' : 'read-only'} access. Up to ${MAX_ACTIVE_TOKENS} active at once.`}
                tokens={myTokens}
                showOwner={false}
                working={working}
                onRevoke={handleRevokeToken}
                action={!creating && config.mcpEnabled && (
                  <button
                    onClick={() => { setCreating(true); setRevealedToken(null); }}
                    disabled={working || myActiveCount >= MAX_ACTIVE_TOKENS}
                    className="flex-shrink-0 flex items-center justify-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-50"
                  >
                    <Plus size={16} /> New token
                  </button>
                )}
              >
                {creating && <CreateTokenForm working={working} onCreate={handleCreateToken} onCancel={() => setCreating(false)} />}
                {revealedToken && <RevealedToken value={revealedToken} copied={copied} onCopy={copyToken} onDismiss={() => setRevealedToken(null)} />}
              </TokensCard>
            )}

            {canManageAll && (
              <TokensCard
                title="Member Tokens"
                description="Tokens other members hold on this project. Revoke any you no longer recognise."
                tokens={otherTokens}
                showOwner
                working={working}
                onRevoke={handleRevokeToken}
              />
            )}

            {config?.mcpEnabled && config.accessLevel && (
              <SnippetCard
                configSnippet={configSnippet}
                snippetCopied={snippetCopied}
                onCopySnippet={copySnippet}
                writeEnabled={canWrite}
              />
            )}

            <FlowGuide />

            <SkillsSection
              skills={skills}
              loading={skillsLoading}
              error={skillsError}
              downloadingId={downloadingSkillId}
              onDownload={handleDownloadSkill}
            />
          </>
        )}
      </div>
    </div>
  );
};
